import React, { useState, useEffect, useCallback } from 'react';
import './ATM.css';

const RESOURCE = 'arketype_Fleeca';

function isIngame() {
  return (
    typeof window.invokeNative !== 'undefined' ||
    navigator.userAgent.includes('CFX') ||
    navigator.userAgent.includes('FiveM')
  );
}

function closeATM() {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: 'close_atm' }, '*');
  }
  fetch(`https://${RESOURCE}/closeATM`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  }).catch(() => {});
}

function fmt(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

export default function ATM() {
  const params     = new URLSearchParams(window.location.search);
  const token      = params.get('token');
  const identifier = params.get('identifier');
  const initBank   = parseFloat(params.get('bank')) || 0;
  const initCash   = parseFloat(params.get('cash')) || 0;
  const initError  = params.get('error') || null;

  let initCompanies = [];
  try { initCompanies = JSON.parse(params.get('companies') || '[]'); } catch(_) {}

  const allowed = isIngame() && (token || initError);

  const [bank, setBank]             = useState(initBank);
  const [cash, setCash]             = useState(initCash);
  const [mode, setMode]             = useState('deposit');
  const [input, setInput]           = useState('');
  const [message, setMessage]       = useState({ text: 'Entrez le montant à traiter', type: 'info' });
  const [result, setResult]         = useState(null);
  const [companies]                 = useState(initCompanies);
  const [selectedCompany, setSelectedCompany] = useState(initCompanies[0] || null);
  const [atmError, setAtmError]     = useState(initError);

  useEffect(() => {
    if (!allowed) return;
    const handler = (e) => { if (e.key === 'Escape') closeATM(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [allowed]);

  const changeMode = useCallback((m) => {
    setMode(m);
    setInput('');
    const hints = {
      deposit:          'Déposez vos espèces sur votre compte bancaire.',
      withdraw:         'Retirez de l\'argent depuis votre compte.',
      'company-deposit':  'Déposez vos espèces sur le compte de votre entreprise.',
      'company-withdraw': 'Retirez des espèces depuis le compte entreprise.',
    };
    setMessage({ text: hints[m] || '', type: 'info' });
  }, []);

  const keyPress = useCallback((key) => {
    if (result) return;
    setInput(prev => {
      if (key === 'clear') return '';
      if (key === 'del')   return prev.slice(0, -1);
      if (prev.length >= 9) return prev;
      if (prev === '0') return key;
      return prev + key;
    });
    setMessage({ text: '', type: 'info' });
  }, [result]);

  const setQuick = useCallback((amount) => {
    if (result) return;
    setInput(String(amount));
    setMessage({ text: '', type: 'info' });
  }, [result]);

  const displayVal = input ? parseInt(input, 10) : 0;

  const confirm = useCallback(async () => {
    if (result) return;
    const amount = parseInt(input, 10);
    if (!amount || amount <= 0) {
      setMessage({ text: 'Veuillez entrer un montant valide', type: 'error' });
      return;
    }

    if ((mode === 'company-deposit' || mode === 'company-withdraw') && !selectedCompany) {
      setMessage({ text: 'Aucune entreprise sélectionnée', type: 'error' });
      return;
    }

    setResult('loading');

    let nuiAction, body;
    if (mode === 'deposit') {
      nuiAction = 'atmDeposit';
      body = { token, identifier, amount };
    } else if (mode === 'withdraw') {
      nuiAction = 'atmWithdraw';
      body = { token, identifier, amount };
    } else if (mode === 'company-deposit') {
      nuiAction = 'atmCompanyDeposit';
      body = { token, identifier, amount, account_name: selectedCompany.account_name };
    } else {
      nuiAction = 'atmCompanyWithdraw';
      body = { token, identifier, amount, account_name: selectedCompany.account_name };
    }

    try {
      const resp = await fetch(`https://${RESOURCE}/${nuiAction}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (data.error) {
        setResult({ ok: false, text: data.error || 'Erreur' });
        setTimeout(() => { setResult(null); setMessage({ text: data.error || 'Erreur', type: 'error' }); }, 2500);
        return;
      }

      if (mode === 'deposit') {
        setBank(data.new_balance);
        setCash(data.cash);
        setResult({ ok: true, text: 'Dépôt effectué', sub: `+${fmt(amount)} · Solde banque : ${fmt(data.new_balance)}` });
      } else if (mode === 'withdraw') {
        setBank(data.new_balance);
        setCash(data.cash);
        setResult({ ok: true, text: 'Retrait effectué', sub: `-${fmt(amount)} · Solde banque : ${fmt(data.new_balance)}` });
      } else if (mode === 'company-deposit') {
        setCash(data.cash);
        if (selectedCompany) setSelectedCompany(prev => ({ ...prev, money: data.new_company_balance }));
        setResult({ ok: true, text: 'Dépôt entreprise effectué', sub: `+${fmt(amount)} → ${selectedCompany?.society_name || ''}` });
      } else {
        setCash(data.cash);
        if (selectedCompany) setSelectedCompany(prev => ({ ...prev, money: data.new_company_balance }));
        setResult({ ok: true, text: 'Retrait entreprise effectué', sub: `-${fmt(amount)} ← ${selectedCompany?.society_name || ''}` });
      }

      setInput('');
      setTimeout(() => { setResult(null); setMessage({ text: 'Transaction réussie !', type: 'success' }); }, 2800);
    } catch {
      setResult({ ok: false, text: 'Erreur de connexion' });
      setTimeout(() => { setResult(null); setMessage({ text: 'Erreur de connexion', type: 'error' }); }, 2500);
    }
  }, [input, mode, result, token, identifier, selectedCompany]);

  if (atmError) {
    return (
      <div className="atm-page atm-page-error">
        <div className="atm-error-card">
          <div className="atm-error-icon"><i className="fa-solid fa-building-columns" /></div>
          <div className="atm-error-title">Fleeca Bank</div>
          <div className="atm-error-subtitle">Distributeur Automatique</div>
          <div className="atm-error-divider" />
          <div className="atm-error-icon-alert"><i className="fa-solid fa-triangle-exclamation" /></div>
          <div className="atm-error-msg">Merci de vous rendre en banque afin d'ouvrir votre compte.</div>
          <button className="atm-error-close" onClick={closeATM}>
            <i className="fa-solid fa-xmark" /> Fermer
          </button>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="atm-page">
        <div className="atm-denied-card">
          <i className="fa-solid fa-lock" />
          <p>Acces reserve aux joueurs connectes en jeu.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="atm-page">

      {/* ── Colonne gauche : infos ── */}
      <div className="atm-left">

        <div className="atm-brand">
          <div className="atm-brand-icon"><i className="fa-solid fa-building-columns" /></div>
          <div>
            <div className="atm-brand-title">Fleeca Bank</div>
            <div className="atm-brand-sub">Distributeur Automatique</div>
          </div>
        </div>

        <div className="atm-balance-card">
          <div className="atm-balance-card-bg" />
          <div className="atm-balance-card-content">
            <span className="atm-balance-label">Solde bancaire</span>
            <div className="atm-balance-amount">{fmt(bank)}</div>
            <div className="atm-balance-row-cash">
              <i className="fa-solid fa-money-bill-wave" />
              <span>Espèces :</span>
              <strong>{fmt(cash)}</strong>
            </div>
          </div>
        </div>

        <div className="atm-tabs">
          <button className={`atm-tab${mode === 'deposit' ? ' active' : ''}`} onClick={() => changeMode('deposit')}>
            <i className="fa-solid fa-arrow-up" /> Dépôt
          </button>
          <button className={`atm-tab${mode === 'withdraw' ? ' active' : ''}`} onClick={() => changeMode('withdraw')}>
            <i className="fa-solid fa-arrow-down" /> Retrait
          </button>
          {companies.length > 0 && (
            <button className={`atm-tab atm-tab-company${(mode === 'company-deposit' || mode === 'company-withdraw') ? ' active' : ''}`} onClick={() => changeMode('company-deposit')}>
              <i className="fa-solid fa-briefcase" /> Société
            </button>
          )}
        </div>

        {(mode === 'company-deposit' || mode === 'company-withdraw') && (
          <div className="atm-subtabs">
            <button className={`atm-subtab${mode === 'company-deposit' ? ' active' : ''}`} onClick={() => changeMode('company-deposit')}>
              <i className="fa-solid fa-arrow-up" /> Dépôt
            </button>
            <button className={`atm-subtab${mode === 'company-withdraw' ? ' active' : ''}`} onClick={() => changeMode('company-withdraw')}>
              <i className="fa-solid fa-arrow-down" /> Retrait
            </button>
          </div>
        )}

        {(mode === 'company-deposit' || mode === 'company-withdraw') && (
          <div className="atm-company-select">
            <div className="atm-company-select-label"><i className="fa-solid fa-briefcase" /> Choisir l'entreprise</div>
            {companies.map(c => (
              <button
                key={c.account_name}
                className={`atm-company-option${selectedCompany?.account_name === c.account_name ? ' selected' : ''}`}
                onClick={() => setSelectedCompany(c)}
              >
                <div className="atm-company-option-left">
                  <div className="atm-company-option-name">{c.society_name || c.account_name.replace('society_', '')}</div>
                  <div className="atm-company-option-job">{c.job}</div>
                </div>
                <div className="atm-company-option-bal">{fmt(c.money)}</div>
              </button>
            ))}
          </div>
        )}

        {!(mode === 'company-deposit' || mode === 'company-withdraw') && (
          <div className="atm-mode-hint">
            {mode === 'deposit'
              ? 'Déposez vos espèces sur votre compte bancaire.'
              : 'Retirez de l\'argent depuis votre compte.'}
          </div>
        )}

        <div className="atm-left-spacer" />

        <button className="atm-close-side" onClick={closeATM}>
          <i className="fa-solid fa-xmark" /> Fermer
        </button>

      </div>

      {/* ── Colonne droite : saisie + clavier ── */}
      <div className="atm-right">

        <div className="atm-input-box">
          <span className="atm-input-prefix">$</span>
          <div className="atm-input-val">{displayVal > 0 ? displayVal.toLocaleString('en-US') : '0'}</div>
          <div className="atm-input-cursor" />
        </div>

        <div className="atm-message-slot">
          {message.text && (
            <div className={`atm-message atm-message-${message.type}`}>
              {message.type === 'error'   && <i className="fa-solid fa-circle-exclamation" />}
              {message.type === 'success' && <i className="fa-solid fa-circle-check" />}
              {message.type === 'info'    && <i className="fa-solid fa-circle-info" />}
              {message.text}
            </div>
          )}
        </div>

        <div className="atm-quick">
          {[500, 1000, 2500, 5000, 10000, 50000].map(v => (
            <button key={v} className="atm-quick-btn" onClick={() => setQuick(v)}>
              {fmt(v)}
            </button>
          ))}
        </div>

        <div className="atm-keypad-grid">
          {['1','2','3','4','5','6','7','8','9'].map(k => (
            <button key={k} className="atm-key" onClick={() => keyPress(k)}>{k}</button>
          ))}
          <button className="atm-key atm-key-clear" onClick={() => keyPress('clear')}>CLR</button>
          <button className="atm-key" onClick={() => keyPress('0')}>0</button>
          <button className="atm-key atm-key-del" onClick={() => keyPress('del')}>
            <i className="fa-solid fa-delete-left" />
          </button>
        </div>

        <button className="atm-confirm-btn" onClick={confirm} disabled={!!result}>
          {result === 'loading'
            ? <><i className="fa-solid fa-spinner atm-spin" /> Traitement...</>
            : <><i className="fa-solid fa-check" /> Valider la transaction</>
          }
        </button>

      </div>

      {result && result !== 'loading' && (
        <div className="atm-result-overlay">
          <div className={`atm-result-icon ${result.ok ? 'ok' : 'err'}`}>
            <i className={`fa-solid ${result.ok ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
          </div>
          <div className="atm-result-text">{result.text}</div>
          {result.sub && <div className="atm-result-sub">{result.sub}</div>}
        </div>
      )}

    </div>
  );
}
